import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Select, Spin} from 'antd';
import {
  GET_FINANCIAL_INSTRUMENT_OPTIONS,
  GET_FINANCIAL_INSTRUMENT_OPTION_BY_ID,
} from 'graphql/queries/assetManagement';
import {instrumentKeyLabel} from 'pages/AssetManagement/modules/instrumentKeys';
import styles from './financialInstrumentPicker.module.scss';

/**
 * One picker for every Instrument v2 destination.
 *
 * Both the notification composer and the announcement composer had their own
 * ad-hoc instrument selects, each covering only bonds and mutual funds, each
 * refetching on its own terms — and stocks were picked from a *company* list,
 * which is the thing the communication handover's first rule forbids.
 *
 * Configure it by target kind; the asset class and instrument key are fully
 * determined by that, so no separate asset-class control is needed.
 *
 * The stored form value is the instrument `id` and nothing else. The full
 * selected option is handed back through `onSelectOption` so a review screen
 * can show a label without re-querying.
 */

/** Target kind -> picker variables. Straight from the handover's §3.2. */
export const INSTRUMENT_TARGETS = {
  PUBLIC_EQUITY: {
    assetClassKey: 'EQUITIES',
    instrumentKey: 'PUBLIC_EQUITY',
    label: 'Stock / public equity',
  },
  IPO: {assetClassKey: 'EQUITIES', instrumentKey: 'IPO', label: 'IPO'},
  PRIVATE_EQUITY: {
    assetClassKey: 'EQUITIES',
    instrumentKey: 'PRIVATE_EQUITY',
    label: 'Startup / private equity',
  },
  BOND: {assetClassKey: 'FIXED_INCOMES', instrumentKey: 'BOND', label: 'Bond'},
  MUTUAL_FUND: {
    assetClassKey: 'FUNDS',
    instrumentKey: 'MUTUAL_FUND',
    label: 'Mutual fund',
  },
  VENTURE_FUND: {
    assetClassKey: 'FUNDS',
    instrumentKey: 'VENTURE_FUND',
    label: 'Venture fund',
  },
};

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE = 300;

/** `ticker`, falling back to `offeringName`, then the raw id. */
const secondaryLabel = option =>
  option?.ticker || option?.offeringName || option?.id;

/**
 * Looks one saved instrument up by id and hands it back. Rendered once per id
 * that the current page of results does not include, so a multi-select edit
 * form shows names rather than raw ids.
 */
const ResolveInstrument = ({id, onResolved}) => {
  const {data} = useQuery(GET_FINANCIAL_INSTRUMENT_OPTION_BY_ID, {
    variables: {id},
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
  });
  const option = data?.getFinancialInstrument?.data;
  useEffect(() => {
    if (option?.id) onResolved(option);
  }, [option, onResolved]);
  return null;
};

export const FinancialInstrumentPicker = ({
  targetKind,
  value,
  onChange,
  onSelectOption,
  disabled,
  placeholder,
  // A previously saved instrument that may not be on the first page of
  // results; shown as an option so an edit form renders its own value.
  seededOption,
  // Several instruments: `value` and `onChange` work with an array of ids.
  multiple = false,
}) => {
  const [search, setSearch] = useState('');
  // Options already seen for selected ids, so a chosen instrument keeps its
  // label after the search moves on to other results.
  const [known, setKnown] = useState({});
  const remember = useCallback(
    option =>
      setKnown(prev =>
        prev[option.id] ? prev : {...prev, [option.id]: option},
      ),
    [],
  );
  const [debounced, setDebounced] = useState('');
  const timer = useRef(null);

  const target = INSTRUMENT_TARGETS[targetKind];

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(
      () => setDebounced(search.trim()),
      SEARCH_DEBOUNCE,
    );
    return () => clearTimeout(timer.current);
  }, [search]);

  const {data, loading} = useQuery(GET_FINANCIAL_INSTRUMENT_OPTIONS, {
    variables: {
      input: {
        assetClassKey: target?.assetClassKey,
        instrumentKey: target?.instrumentKey,
        explore: false,
        status: 'ENABLED',
        paginate: true,
        page: 1,
        limit: PAGE_SIZE,
        ...(debounced && {search: debounced}),
      },
    },
    skip: !target,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  const result = data?.getFinancialInstruments;
  const rows = useMemo(
    // A union member the query does not select yields `{}`; those are not
    // instruments.
    () => (result?.data ?? []).filter(row => row?.id),
    [result],
  );

  // An edit form supplies only the saved id, and that instrument is usually
  // outside the first page. Resolve it directly so the Select has a matching
  // option to render — otherwise the field looks empty and the next save
  // silently drops the destination.
  const needsResolve = Boolean(
    !multiple &&
      value &&
      !seededOption?.id &&
      !rows.some(row => row.id === value),
  );
  const selectedIds = multiple ? (Array.isArray(value) ? value : []) : [];
  const unresolvedIds = selectedIds.filter(
    id => !known[id] && !rows.some(row => row.id === id),
  );

  const {data: resolvedData} = useQuery(GET_FINANCIAL_INSTRUMENT_OPTION_BY_ID, {
    variables: {id: value},
    skip: !needsResolve,
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
  });

  const resolved = resolvedData?.getFinancialInstrument?.data;

  // Keep the saved value selectable even when it falls outside the current
  // page or search.
  const options = useMemo(() => {
    if (multiple) {
      const pinned = selectedIds
        .map(id => known[id])
        .filter(option => option && !rows.some(row => row.id === option.id));
      return [...pinned, ...rows];
    }
    const pinned = seededOption?.id
      ? seededOption
      : resolved?.id
      ? resolved
      : null;
    if (!pinned) return rows;
    if (rows.some(row => row.id === pinned.id)) return rows;
    return [pinned, ...rows];
  }, [multiple, selectedIds, known, rows, seededOption, resolved]);

  const handleChange = next => {
    if (multiple) {
      const ids = next ?? [];
      ids.forEach(id => {
        const option = options.find(item => item.id === id);
        if (option) remember(option);
      });
      onChange?.(ids);
      return;
    }
    onChange?.(next);
    onSelectOption?.(options.find(option => option.id === next) ?? null);
  };

  return (
    <>
      {unresolvedIds.map(id => (
        <ResolveInstrument key={id} id={id} onResolved={remember} />
      ))}
      <Select
        showSearch
        allowClear
        mode={multiple ? 'multiple' : undefined}
        value={multiple ? selectedIds : value || undefined}
        disabled={disabled || !target}
        loading={loading}
        onChange={handleChange}
        onSearch={setSearch}
        // Search is served by the API, so every returned option is kept.
        filterOption={false}
        placeholder={
          placeholder ??
          (target
            ? `Search ${target.label.toLowerCase()}`
            : 'Select a type first')
        }
        notFoundContent={
          loading ? <Spin size="small" /> : 'No matching instruments'
        }
        className={styles.picker}
        optionLabelProp="label">
        {options.map(option => (
          <Select.Option
            key={option.id}
            value={option.id}
            label={option.name}
            // The handover disables anything not ENABLED; the backend validates
            // the id against the destination type and would reject it anyway.
            disabled={option.status && option.status !== 'ENABLED'}>
            <div className={styles.option}>
              {option.icon ? (
                <img src={option.icon} alt="" className={styles.optionIcon} />
              ) : (
                <span className={styles.optionIcon} />
              )}
              <span className={styles.optionText}>
                <span className={styles.optionName}>{option.name}</span>
                <span className={styles.optionSub}>
                  {secondaryLabel(option)}
                  {option.status && option.status !== 'ENABLED'
                    ? ` · ${option.status.toLowerCase()}`
                    : ''}
                </span>
              </span>
              {option.key && (
                <span className={styles.optionKey}>
                  {instrumentKeyLabel(option.key)}
                </span>
              )}
            </div>
          </Select.Option>
        ))}
      </Select>
    </>
  );
};

export default FinancialInstrumentPicker;
